import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import ListingEditModal from "../components/ListingEditModal";

const PAGE_SIZE = 25;

export default function ListingSearchPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [saleOrRental, setSaleOrRental] = useState("");
  const [ownershipType, setOwnershipType] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("created_date");
  const [sortDir, setSortDir] = useState("desc");
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);

  const [editModalListing, setEditModalListing] = useState(null);

  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => setNeighborhoods(data.neighborhoods || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, saleOrRental, ownershipType, statusFilter, sortBy, sortDir, selectedNeighborhoods]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE, status_filter: statusFilter, sort_by: sortBy, sort_dir: sortDir });
      if (search) params.append("search", search);
      if (saleOrRental) params.append("sale_or_rental", saleOrRental);
      if (ownershipType) params.append("ownership_type", ownershipType);
      if (selectedNeighborhoods.length > 0) params.append("neighborhood_ids", selectedNeighborhoods.join(","));
      const data = await authFetch(`/listings/search?${params}`);
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("Error searching listings:", e);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => { setPage(1); setSearch(searchInput.trim()); };
  const resetFilters = () => {
    setSearchInput(""); setSearch(""); setSaleOrRental(""); setOwnershipType("");
    setStatusFilter("active"); setSortBy("created_date"); setSortDir("desc");
    setSelectedNeighborhoods([]); setPage(1);
  };
  const onFilter = (setter) => (e) => { setPage(1); setter(e.target.value); };
  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("desc"); }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const inputStyle = { width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "13px", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" };
  const th = (field, label, sortable = true) => (
    <th
      onClick={sortable ? () => toggleSort(field) : undefined}
      style={{ textAlign: "left", padding: "8px 10px", fontSize: "11px", fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase", whiteSpace: "nowrap", cursor: sortable ? "pointer" : "default", userSelect: "none" }}
    >
      {label}{sortable && sortBy === field ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  const td = { padding: "8px 10px", fontSize: "13px", color: "#333", borderBottom: "1px solid #eee", verticalAlign: "top" };
  const actionBtn = (bg) => ({ padding: "3px 9px", fontSize: "11px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ padding: "20px", maxWidth: "1700px", margin: "0 auto" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700, margin: "0 0 16px 0", color: "#222" }}>Listings</h1>

        {/* Filters */}
        <div style={{ padding: "16px", backgroundColor: "#fff", borderRadius: "8px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
            <NeighborhoodSelect
              neighborhoods={neighborhoods}
              selected={selectedNeighborhoods}
              onChange={(v) => { setPage(1); setSelectedNeighborhoods(v); }}
              placeholder="All Neighborhoods"
              gridColumn="span 2"
            />
            <div>
              <label style={labelStyle}>Address / Listing ID</label>
              <input type="text" placeholder="Search…" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={saleOrRental} onChange={onFilter(setSaleOrRental)} style={{ ...inputStyle, backgroundColor: "#fff" }}>
                <option value="">Sale & Rental</option>
                <option value="S">Sale</option>
                <option value="R">Rental</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Ownership</label>
              <select value={ownershipType} onChange={onFilter(setOwnershipType)} style={{ ...inputStyle, backgroundColor: "#fff" }}>
                <option value="">All</option>
                <option value="condo">Condo</option>
                <option value="coop">Co-op</option>
                <option value="rental">Rental Bldg</option>
                <option value="townhouse">Townhouse</option>
                <option value="multi">Multi Family</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={statusFilter} onChange={onFilter(setStatusFilter)} style={{ ...inputStyle, backgroundColor: "#fff" }}>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="all">All</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
              <button onClick={applySearch} disabled={loading}
                style={{ padding: "8px 18px", backgroundColor: "#000", color: "#fff", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: 500, cursor: loading ? "wait" : "pointer" }}>
                {loading ? "…" : "Search"}
              </button>
              <button onClick={resetFilters}
                style={{ padding: "8px 14px", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Count + pagination */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>
            {total > 0 ? `${((page - 1) * PAGE_SIZE + 1).toLocaleString()}–${Math.min(total, page * PAGE_SIZE).toLocaleString()} of ${total.toLocaleString()}` : "No results"}
          </div>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>

        {/* Results */}
        <div style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {th("address", "Address")}
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Type</th>
                {th("neighborhood", "Neighborhood")}
                {th("bedrooms", "Bd/Ba/Sqft")}
                {th("list_price", "Price")}
                {th("predicted_rent", "Pred. Rent")}
                {th("created_date", "Listed")}
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Status</th>
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>Loading…</td></tr>
              ) : listings.length === 0 ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>No listings match these filters.</td></tr>
              ) : (
                listings.map((l) => (
                  <tr key={l.id}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                    <td style={{ ...td, maxWidth: 340 }}>
                      <div style={{ fontWeight: 600, color: "#222" }}>
                        {l.address}{l.unit_number ? ` #${l.unit_number}` : ""}
                        {l.verified && (
                          <span title="Data verified" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#2e7d32", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>✓ Verified</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#888" }}>{l.sale_or_rental === "S" ? "Sale" : "Rental"} · #{l.id}</div>
                    </td>
                    <td style={td}>{l.condo_coop}</td>
                    <td style={td}>{l.neighborhood || "—"}</td>
                    <td style={td}>
                      {(l.bedrooms ?? "?")}bd · {(l.bathrooms ?? "?")}ba
                      <div style={{ fontSize: 12, color: "#777" }}>{l.sqft ? l.sqft.toLocaleString() + " sqft" : "—"}</div>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{l.list_price ? `$${l.list_price.toLocaleString()}` : "—"}</td>
                    <td style={td}>
                      {l.predicted_rent ? <><span style={{ fontWeight: 600, color: "#1976d2" }}>${l.predicted_rent.toLocaleString()}</span>{l.cap_rate != null && <div style={{ fontSize: 11, color: "#888" }}>cap {l.cap_rate}%</div>}</> : <span style={{ color: "#bbb" }}>—</span>}
                    </td>
                    <td style={td}>{l.created_date ? new Date(l.created_date).toLocaleDateString() : "—"}</td>
                    <td style={{ ...td, fontSize: 12 }}>{l.listing_status}</td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button style={actionBtn("#1976d2")} onClick={() => navigate(`/listing/${l.id}`)}>Details</button>
                        <button style={actionBtn("#455a64")} onClick={() => setEditModalListing({ id: l.id })}>Edit</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>
      </div>

      {editModalListing && (
        <ListingEditModal
          listing={editModalListing}
          onClose={() => setEditModalListing(null)}
          onSave={() => { setEditModalListing(null); loadListings(); }}
          onVerify={() => { setEditModalListing(null); loadListings(); }}
        />
      )}
    </div>
  );
}

function Pager({ page, totalPages, loading, setPage }) {
  const btn = (disabled) => ({ padding: "5px 12px", fontSize: "13px", border: "1px solid #ccc", borderRadius: "4px", background: disabled ? "#f5f5f5" : "#fff", color: disabled ? "#aaa" : "#333", cursor: disabled ? "not-allowed" : "pointer" });
  const atStart = page <= 1, atEnd = page >= totalPages;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <button style={btn(atStart || loading)} disabled={atStart || loading} onClick={() => setPage(page - 1)}>‹ Prev</button>
      <span style={{ fontSize: "13px", color: "#666" }}>Page {page} of {totalPages}</span>
      <button style={btn(atEnd || loading)} disabled={atEnd || loading} onClick={() => setPage(page + 1)}>Next ›</button>
    </div>
  );
}
